import {
  address as parseSolanaAddress,
  isSignature,
  type Address,
  type Signature,
} from '@solana/kit';
import {
  isAddress as _isEthereumAddress,
  Address as _EthereumAddress,
  Hash as _EthereumHash,
  isHash as _isEthereumHash,
  createPublicClient,
  http,
} from 'viem';

import { logger } from '@/lib/logger';

import { solana, SUPPORTED_CHAINS_BY_CHAIN_ID, SupportedChainIDs } from '@/constants/chain';

import { getSolanaRpc } from './solana';

/** System Program address; system-owned accounts are treated as wallet (EOA-equivalent) addresses. */
const SYSTEM_PROGRAM_ADDRESS = '11111111111111111111111111111111';

export type SolanaAddress<TAddress extends string = string> = Address<TAddress>;

export type EthereumAddress = _EthereumAddress;

// Address type for any supported chain in the app
export type ChainAddress = EthereumAddress | SolanaAddress;

export const isEthereumAddress = (address: string) => {
  return _isEthereumAddress(address);
};

export function isSolanaAddress(address: string): boolean {
  try {
    parseSolanaAddress(address);
    return true;
  } catch {
    return false;
  }
}

export function isValidAddressForChain(address: string, chainId: number) {
  return chainId === solana.id ? isSolanaAddress(address) : isEthereumAddress(address);
}

// Check that the address is a valid address of any chain supported by the app
export const isChainAddress = (address: string | undefined): address is ChainAddress => {
  if (address === undefined) return false;
  return isEthereumAddress(address) || isSolanaAddress(address);
};

export type EthereumHash = _EthereumHash;

// Keep the chain-specific name while preserving kit's nominal signature brand.
export type SolanaHash = Signature;

export type ChainHash = EthereumHash | SolanaHash;

export const isEthereumHash = (value: string): value is EthereumHash => {
  return _isEthereumHash(value);
};

// kit's isSignature throws (rather than returning false) on signature-length
// strings containing non-base58 characters.
export const isSolanaHash = (value: string): value is SolanaHash => {
  try {
    return isSignature(value);
  } catch {
    return false;
  }
};

// Check if an Ethereum address is a wallet address (EOA) instead of a contract address
export async function isEthereumWalletAddress(
  address: string,
  chainId: SupportedChainIDs,
): Promise<boolean> {
  if (!isEthereumAddress(address)) {
    return false;
  }

  try {
    const chain = SUPPORTED_CHAINS_BY_CHAIN_ID[chainId];
    const publicClient = createPublicClient({
      chain,
      transport: http(),
    });

    const code = await publicClient.getCode({ address: address as EthereumAddress });
    return !code || code === '0x';
  } catch (error) {
    logger.error('Error checking Ethereum wallet address:', error);
    return false;
  }
}

// Check if a Solana address is a wallet address (system-owned account) instead of a program account
export async function isSolanaWalletAddress(address: string): Promise<boolean> {
  if (!isSolanaAddress(address)) {
    return false;
  }

  try {
    const { value } = await getSolanaRpc()
      .getAccountInfo(parseSolanaAddress(address), { encoding: 'base64' })
      .send();

    // If account doesn't exist on-chain, assume it's a wallet address.
    // This may be incorrect (could be unused PDA or random key), but we accept
    // this trade-off to allow fresh wallet addresses.
    if (!value) {
      return true;
    }

    // Check if owned by System Program (wallet addresses)
    return value.owner === SYSTEM_PROGRAM_ADDRESS;
  } catch (error) {
    logger.error('Error checking Solana wallet address:', error);
    return false;
  }
}
